import * as React from "react";
import {
  Cloud,
  Download,
  FileSpreadsheet,
  FileText,
  HardDrive,
  Loader2,
  PencilLine,
  Presentation,
} from "lucide-react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { cn } from "../../lib/cn";
import { AndoLogo } from "../AndoLogo";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import {
  DestinationPicker,
  type DestinationChoice,
  type DestinationFolderItem,
  type SaveDestination,
} from "./DestinationPicker";
import { SlideOutlineEditor, type SlideObj } from "./SlideOutlineEditor";
import {
  StyleTemplatePicker,
  type BrandKitDefaultOption,
  type OoxmlTemplateOption,
  type StylePresetOption,
  type StyleTemplateValue,
} from "./StyleTemplatePicker";

/**
 * DocumentCreatePreview — visual preview for ``document_create`` action
 * proposals (BE: ``DocumentCreatePreview`` in
 * ``src/models/action_models.py``).
 *
 * Three layers (top → bottom):
 *
 *   1. **File card** — orange icon badge + filename + document-type label.
 *   2. **Content preview** — proper markdown rendering (react-markdown +
 *      remark-gfm) inside an editorial card. Each ``##`` section is
 *      wrapped in a hover-revealed "Modifica con Ando" affordance: hover
 *      a section → orange pencil chip appears in the top-right → click
 *      → inline refinement panel expands below the heading with a
 *      free-form instruction textarea + ``Rigenera`` CTA. The submit
 *      bubbles up via the optional ``onRefineSection`` prop so the
 *      caller can wire the BE refine endpoint (planned May 25, 2026).
 *   3. **Destinations** — chip strip (Drive / OneDrive / SharePoint /
 *      Download / MinIO archive) showing where the file will be saved
 *      on Conferma.
 *
 * SOTA UX patterns honoured (audited via ``ui-ux-pro-max`` skill, May 25,
 * 2026): editorial typography scale, hover-row reveal (Notion / Linear),
 * `prefers-reduced-motion` aware, keyboard reachable refinement panel,
 * focus rings on every actionable element, content max-width 680px for
 * readability.
 *
 * Pure presentational — no store, no router. Pure props in.
 */

/**
 * Schema fields this preview renders inline (the editable filename header +
 * the per-slide ``SlideOutlineEditor``). Passed to ``ActionDrawer`` as
 * ``previewOwnedFields`` so the stacked schema-driven ``ActionFieldEditor``
 * SKIPS them — otherwise a presentation ``document_create`` proposal shows the
 * ``title`` + ``slides`` (SLIDE_LIST) fields TWICE: once here (the loved
 * editor, add-slide button at the bottom) and once stacked above (a duplicate
 * "Aggiungi slide" button + a spurious "Campo obbligatorio" because the
 * stacked copy's value mirror is empty). Mirrors
 * ``EMAIL_PREVIEW_OWNED_FIELDS`` / ``MESSAGE_PREVIEW_OWNED_FIELDS``.
 */
export const DOCUMENT_CREATE_PREVIEW_OWNED_FIELDS = ["title", "slides"] as const;

const DOCUMENT_TYPE_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  document: FileText,
  spreadsheet: FileSpreadsheet,
  presentation: Presentation,
  csv: FileSpreadsheet,
  pdf: FileText,
  html: FileText,
};

const DOCUMENT_TYPE_LABEL: Record<string, MessageDescriptor> = {
  document: msg`Documento Word`,
  spreadsheet: msg`Foglio Excel`,
  presentation: msg`Presentazione PowerPoint`,
  csv: msg`CSV`,
  pdf: msg`PDF`,
  html: msg`HTML`,
};

const DESTINATION_LABEL: Record<string, MessageDescriptor> = {
  google_drive: msg`Google Drive`,
  onedrive: msg`OneDrive`,
  sharepoint: msg`SharePoint`,
  download: msg`Download diretto`,
  minio: msg`Archivio Ando`,
};

const DESTINATION_ICON: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  google_drive: Cloud,
  onedrive: Cloud,
  sharepoint: Cloud,
  download: Download,
  minio: HardDrive,
};

/**
 * Bubbled-up payload for the per-section refinement request. Caller is
 * responsible for plumbing this into a BE ``refine_section`` call —
 * `DocumentCreatePreview` itself only handles the UI state machine.
 */
export interface DocumentCreateRefineInput {
  readonly sectionHeading: string;
  readonly instruction: string;
}

/**
 * Interactive destination-picker contract. When omitted the component
 * falls back to the read-only chip strip (storybook / non-confirm
 * surfaces). When provided, the picker REPLACES the strip and bubbles
 * the choice up to the caller, which is responsible for forwarding
 * ``save_destination`` + ``folder_id`` inside the execute
 * ``modifications`` map. The picker stays fully controlled — this
 * component never owns ``value``.
 *
 * The folder list itself is also fully delegated: the caller fetches
 * via ``useDriveFolders`` (or any equivalent) and passes ``folders``
 * + ``isLoadingFolders``. This keeps ``@ando/ui`` package data-free
 * and Storybook-friendly. See [DestinationPicker.tsx](./DestinationPicker.tsx)
 * for the picker's own UX contract.
 */
export interface DocumentCreateDestinationInteractive {
  readonly value: DestinationChoice;
  readonly onChange: (next: DestinationChoice) => void;
  readonly folders: ReadonlyArray<DestinationFolderItem>;
  readonly isLoadingFolders?: boolean;
  /** Open the integrations page from the "no folders" empty state. */
  readonly onOpenIntegrations?: () => void;
  /** Disable the picker while the parent's submit is in-flight. */
  readonly disabled?: boolean;
}

/**
 * Per-slide editable outline contract — present ONLY for presentation
 * ``document_create`` proposals whose BE payload-schema shipped a
 * ``slide_list`` field. When provided, the read-only markdown outline is
 * replaced by the controlled ``SlideOutlineEditor`` and the filename
 * becomes an editable input. Both edits flow back to the caller, which
 * commits them into the execute ``modifications`` map ({ title, slides }).
 *
 * Omitting the prop preserves today's behaviour (read-only markdown +
 * optional hover-section refiner) for Word / xlsx / pdf document_create and
 * for older presentation proposals served before the slide_list rollout.
 */
export interface DocumentCreateSlideOutline {
  readonly slides: ReadonlyArray<SlideObj>;
  /** Allowed layout names from the BE ``slide_list`` field ``options``. */
  readonly layoutOptions: ReadonlyArray<string>;
  readonly onSlidesChange: (next: SlideObj[]) => void;
  /** Editable filename (BE ``title`` text field). */
  readonly title: string;
  readonly onTitleChange: (next: string) => void;
  /** Disable inputs while the parent's submit is in-flight. */
  readonly disabled?: boolean;
}

/**
 * Interactive style / template picker contract. When provided, the
 * ``<StyleTemplatePicker>`` is rendered inside the card (between the file
 * header and the content) so the user can choose HOW the file is created
 * (visual style preset OR a fixed-fill {{placeholder}} template) before
 * confirming. The choice is mutually exclusive and bubbles up to the
 * caller, which writes it into the execute ``content`` map as
 * ``_style_id`` / ``_ooxml_template_slug`` (HITL-editable, survives the
 * proposal → execute round-trip — same mechanism as the destination pick).
 *
 * Fully controlled + data-free (Storybook-friendly): the caller fetches
 * ``styles`` (``useStyles``) + ``templates`` (``listOoxmlTemplates``).
 */
export interface DocumentCreateStyleTemplateInteractive {
  readonly value: StyleTemplateValue;
  readonly onChange: (next: StyleTemplateValue) => void;
  readonly styles: ReadonlyArray<StylePresetOption>;
  readonly templates: ReadonlyArray<OoxmlTemplateOption>;
  /**
   * The user's effective Brand Kit default — rendered as the FIRST, pre-selected
   * card in the Template tab. ``null`` when the user has no kit.
   */
  readonly brandKit?: BrandKitDefaultOption | null;
  readonly brandKitLoading?: boolean;
  readonly stylesLoading?: boolean;
  readonly templatesLoading?: boolean;
  /** Open the settings page from the templates empty state. */
  readonly onOpenTemplateSettings?: () => void;
  /**
   * Learn a brand-new style from a reference file dropped in the Stile tab.
   * When omitted the upload affordance is hidden. The host owns the mutation
   * (``useLearnStyle``) + cache invalidation; on success the new style appears
   * under "I tuoi stili" and is auto-selected.
   */
  readonly onLearnStyle?: (file: File) => void;
  readonly learnStylePending?: boolean;
  readonly learnStyleError?: string | null;
  /** Upload a brand template inline from the gallery (auto-selected on success). */
  readonly onUploadTemplate?: (file: File) => void;
  readonly uploadTemplatePending?: boolean;
  readonly disabled?: boolean;
}

export interface DocumentCreatePreviewProps {
  readonly title: string;
  readonly documentType: string;
  readonly contentPreview: string;
  readonly availableDestinations: ReadonlyArray<string>;
  /**
   * When provided, render the style/template picker inside the card so the
   * user can choose the look before Conferma. See
   * {@link DocumentCreateStyleTemplateInteractive}.
   */
  readonly styleTemplate?: DocumentCreateStyleTemplateInteractive;
  /**
   * Optional async handler invoked when the user submits a per-section
   * refinement. The component awaits the returned promise to drive its
   * own loading state; the caller can ignore or toast on the FE side.
   * Omitting the prop hides the "Modifica con Ando" affordance.
   */
  readonly onRefineSection?: (input: DocumentCreateRefineInput) => Promise<void>;
  /**
   * When provided, render the interactive destination picker instead
   * of the read-only chip strip. May 2026 SOTA — closes the loop on
   * "salva nella folder Drive: <URL>" so the user can pick destination
   * + folder in the UI too (matches the BE's ``modifications`` →
   * ``preview.save_destination`` → default resolution chain).
   */
  readonly destination?: DocumentCreateDestinationInteractive;
  /**
   * When provided, render the per-slide editor instead of the read-only
   * markdown outline (presentation proposals with a ``slide_list`` field).
   */
  readonly slideOutline?: DocumentCreateSlideOutline;
  /**
   * WYSIWYG per-slide thumbnails for a SANDBOX-authored deck (finished .pptx
   * binary, no editable outline). When present — and no editable
   * ``slideOutline`` — the content section shows a grid of the ACTUAL rendered
   * slides instead of the text summary. Each ``url`` is a ``data:image/png``
   * URI. See {@link DocumentCreateSlidePreviews}.
   */
  readonly slidePreviews?: DocumentCreateSlidePreviews;
  readonly className?: string;
}

/**
 * Rendered per-slide thumbnails (sandbox decks). ``images`` are shown in a
 * grid; ``loading`` drives a skeleton while the BE renders the deck. Both empty
 * / false → the read-only summary is used (fail-open).
 */
export interface DocumentCreateSlidePreviews {
  readonly images: ReadonlyArray<{ readonly index: number; readonly url: string }>;
  readonly loading?: boolean;
}

export const DocumentCreatePreview: React.FC<DocumentCreatePreviewProps> = ({
  title,
  documentType,
  contentPreview,
  availableDestinations,
  onRefineSection,
  destination,
  styleTemplate,
  slideOutline,
  slidePreviews,
  className,
}) => {
  const { i18n } = useLingui();
  const Icon = DOCUMENT_TYPE_ICON[documentType] ?? FileText;
  const typeLabelDescriptor = DOCUMENT_TYPE_LABEL[documentType];
  const typeLabel = typeLabelDescriptor ? i18n._(typeLabelDescriptor) : documentType;
  // WYSIWYG thumbnails take over the content section ONLY for a sandbox deck
  // (no editable ``slideOutline``) that has rendered slides OR is still
  // rendering. Otherwise we fall back to the read-only markdown summary.
  const showThumbnails =
    !slideOutline &&
    Boolean(
      (slidePreviews?.images.length ?? 0) > 0 || slidePreviews?.loading,
    );
  const destinations =
    availableDestinations.length > 0 ? availableDestinations : ["minio"];
  // Filter the strings the BE shipped down to the typed enum the picker
  // accepts. Done once here so both branches (interactive / read-only)
  // start from the same narrowed list.
  const renderableDestinations = React.useMemo(
    () =>
      destinations.filter(
        (d): d is SaveDestination =>
          d === "google_drive" ||
          d === "onedrive" ||
          d === "sharepoint" ||
          d === "download" ||
          d === "minio",
      ),
    [destinations],
  );

  // Track which section heading is currently expanded into the
  // refinement panel — null = closed, string = open. Only one section
  // open at a time (single-focus discipline; multi-open clutters the
  // narrative the user is reviewing).
  const [openSection, setOpenSection] = React.useState<string | null>(null);
  const [refineBusy, setRefineBusy] = React.useState(false);
  const [refineText, setRefineText] = React.useState("");
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);

  // Auto-focus the textarea when a panel opens so the user can type
  // immediately. Respects browser focus visibility; the focus ring is
  // applied via `:focus-visible`.
  React.useEffect(() => {
    if (openSection && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [openSection]);

  const handleOpenRefine = React.useCallback((heading: string) => {
    setOpenSection(heading);
    setRefineText("");
  }, []);

  const handleCloseRefine = React.useCallback(() => {
    setOpenSection(null);
    setRefineText("");
  }, []);

  const handleSubmitRefine = React.useCallback(async () => {
    if (!openSection || !refineText.trim() || !onRefineSection) return;
    setRefineBusy(true);
    try {
      await onRefineSection({
        sectionHeading: openSection,
        instruction: refineText.trim(),
      });
      setOpenSection(null);
      setRefineText("");
    } finally {
      setRefineBusy(false);
    }
  }, [openSection, refineText, onRefineSection]);

  // Track the most-recently-rendered H2 heading so each section element
  // can be tagged with its "section title" for the refinement chip — the
  // BE refine endpoint needs to know WHICH section we're rewriting.
  const lastHeadingRef = React.useRef<string>("");

  // Custom Markdown renderers, typography scale + per-H2 section affordance
  // wrap. Deliberately STABLE (deps: only whether refinement is enabled at
  // all): the live open/text/busy state is read by ``SectionWrapper`` via
  // ``RefineContext``, NOT closed over here. Baking ``refineText`` into this
  // memo used to recreate the h1-h4 component identities on every keystroke,
  // which made react-markdown unmount and remount the whole heading+panel
  // subtree, killing the textarea's focus after each character typed (#30).
  const components = React.useMemo<Components>(() => {
    // EVERY heading level (h1–h4) is wrapped in the same section affordance, so
    // ALL sections — not only ``##`` — expose the hover "Modifica con Ando"
    // chip. (The doc mixes #/##/### headings; previously only ## was editable.)
    const makeHeading = (
      headingTag: "h1" | "h2" | "h3" | "h4",
      headingClassName: string,
    ): Components["h2"] =>
      ({ children }) => {
        const heading = String(children);
        lastHeadingRef.current = heading;
        return (
          <SectionWrapper
            heading={heading}
            headingTag={headingTag}
            headingClassName={headingClassName}
            showAffordance={onRefineSection !== undefined}
          >
            {children}
          </SectionWrapper>
        );
      };
    return {
    h1: makeHeading(
      "h1",
      "font-display font-semibold text-[22px] text-ink-1 leading-tight tracking-tight",
    ),
    h2: makeHeading(
      "h2",
      "font-display font-semibold text-[16px] text-ink-1 leading-tight",
    ),
    h3: makeHeading(
      "h3",
      "font-display font-semibold text-[13.5px] text-ink-2 leading-tight",
    ),
    h4: makeHeading(
      "h4",
      "font-medium text-[11px] text-ink-3 uppercase tracking-[0.06em]",
    ),
    p: ({ children }) => (
      <p className="my-2 text-[13.5px] text-ink-1 leading-[1.65]">
        {children}
      </p>
    ),
    strong: ({ children }) => (
      <strong className="font-semibold text-ink-1">{children}</strong>
    ),
    em: ({ children }) => <em className="text-ink-2 italic">{children}</em>,
    ul: ({ children }) => (
      <ul className="my-2 list-disc space-y-1 pl-5 marker:text-ando-orange">
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol className="my-2 list-decimal space-y-1 pl-5 marker:text-ando-orange">
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li className="text-[13.5px] text-ink-1 leading-[1.6]">{children}</li>
    ),
    code: ({ children, ...rest }) => {
      const inline = !(rest as { className?: string }).className;
      if (inline) {
        return (
          <code className="rounded bg-cream-deep/60 px-1.5 py-0.5 font-mono text-[12px] text-ink-1">
            {children}
          </code>
        );
      }
      return (
        <pre className="my-3 overflow-x-auto rounded-md border border-hairline bg-cream-deep/40 p-3 font-mono text-[12px] text-ink-1 leading-relaxed">
          <code>{children}</code>
        </pre>
      );
    },
    a: ({ href, children }) => (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="text-ando-orange underline-offset-2 hover:underline focus-visible:underline"
      >
        {children}
      </a>
    ),
    blockquote: ({ children }) => (
      <blockquote className="my-3 border-ando-orange/40 border-l-2 pl-3 text-ink-2 italic">
        {children}
      </blockquote>
    ),
    hr: () => <hr className="my-4 border-hairline" />,
    table: ({ children }) => (
      <div className="my-3 overflow-x-auto rounded-md border border-hairline">
        <table className="w-full text-[12.5px]">{children}</table>
      </div>
    ),
    th: ({ children }) => (
      <th className="bg-cream-deep/40 px-2 py-1.5 text-left font-medium text-[11.5px] text-ink-2 uppercase tracking-[0.04em]">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="border-hairline border-t px-2 py-1.5 text-ink-1">
        {children}
      </td>
    ),
    };
  }, [onRefineSection]);

  // Live refine-panel state for ``SectionWrapper`` (read via context, see
  // above). Its identity changes every keystroke, and that's fine: it is NOT
  // part of ``components``'s dependency array, so it only re-renders the
  // already-mounted ``SectionWrapper`` instances instead of recreating them.
  const refineContextValue = React.useMemo<RefineContextValue>(
    () => ({
      openSection,
      refineText,
      refineBusy,
      textareaRef,
      onOpen: handleOpenRefine,
      onClose: handleCloseRefine,
      onTextChange: setRefineText,
      onSubmit: handleSubmitRefine,
    }),
    [openSection, refineText, refineBusy, handleOpenRefine, handleCloseRefine, handleSubmitRefine],
  );

  return (
    <div
      className={cn(
        // ONE continuous unified sheet — header · style · content · destinations
        // as hairline-divided rows sharing a single border + focus-ring, instead
        // of the old stacked ``rounded-xl`` cards ("spezzato"). Mirrors
        // ``VendorPreviewShell`` / the unified email compose.
        "flex flex-col rounded-xl border border-hairline bg-white/60 transition-colors focus-within:border-ando-orange/30 focus-within:ring-2 focus-within:ring-ando-orange/40",
        className,
      )}
    >
      {/* File header row */}
      <div className="flex items-start gap-3 px-4 py-3">
        <span
          aria-hidden
          className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-md bg-ando-orange-soft text-ando-orange"
        >
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          {slideOutline ? (
            <input
              type="text"
              aria-label={i18n._(msg`Nome file`)}
              className="w-full bg-transparent font-medium text-[14px] text-ink-1 placeholder:text-ink-3 focus:outline-none disabled:opacity-50"
              value={slideOutline.title}
              placeholder={i18n._(msg`Nome file`)}
              disabled={slideOutline.disabled}
              onChange={(e) => slideOutline.onTitleChange(e.target.value)}
            />
          ) : (
            <div className="truncate font-medium text-[14px] text-ink-1">{title}</div>
          )}
          <div className="mt-0.5 text-[11.5px] text-ink-3">{typeLabel}</div>
        </div>
      </div>

      {/* Style / template picker — "scegli come vuoi che venga creato".
          Integrated between the file header and the content so it reads as
          part of the creation decision, not a bolted-on afterthought. */}
      {styleTemplate ? (
        <div className="border-hairline border-t px-4 py-3">
          <StyleTemplatePicker
            value={styleTemplate.value}
            onChange={styleTemplate.onChange}
            styles={styleTemplate.styles}
            templates={styleTemplate.templates}
            brandKit={styleTemplate.brandKit}
            brandKitLoading={styleTemplate.brandKitLoading}
            stylesLoading={styleTemplate.stylesLoading}
            templatesLoading={styleTemplate.templatesLoading}
            documentType={documentType}
            onOpenTemplateSettings={styleTemplate.onOpenTemplateSettings}
            onLearnStyle={styleTemplate.onLearnStyle}
            learnStylePending={styleTemplate.learnStylePending}
            learnStyleError={styleTemplate.learnStyleError}
            onUploadTemplate={styleTemplate.onUploadTemplate}
            uploadTemplatePending={styleTemplate.uploadTemplatePending}
            disabled={styleTemplate.disabled}
          />
        </div>
      ) : null}

      {/* Content — three modes (first match wins):
          1. per-slide EDITOR      (presentations w/ a ``slide_list`` outline),
          2. WYSIWYG THUMBNAILS     (sandbox deck: finished binary, no outline),
          3. read-only markdown     (Word / xlsx / pdf + legacy proposals). */}
      <div className="border-hairline border-t px-5 py-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="font-medium text-[10px] text-ink-3 uppercase tracking-[0.06em]">
            {slideOutline || showThumbnails
              ? i18n._(msg`Slide`)
              : i18n._(msg`Contenuto proposto`)}
          </div>
          {/* The hover-section refiner only ever worked for the Word-style
              ``##`` outline — suppress its hint for the slide editor / rendered
              thumbnails, which have no per-section text to rewrite. */}
          {!slideOutline && !showThumbnails && onRefineSection ? (
            <div className="inline-flex items-center gap-1 text-[11px] text-ink-3">
              <AndoLogo size={12} aria-hidden />
              {i18n._(msg`Passa sopra a una sezione per modificarla`)}
            </div>
          ) : null}
        </div>
        {slideOutline ? (
          <SlideOutlineEditor
            slides={slideOutline.slides}
            options={slideOutline.layoutOptions}
            onChange={slideOutline.onSlidesChange}
            disabled={slideOutline.disabled}
          />
        ) : showThumbnails ? (
          <SlideThumbnailGrid
            images={slidePreviews?.images ?? []}
            loading={Boolean(slidePreviews?.loading)}
          />
        ) : (
          <div className="mx-auto max-w-[680px] text-ink-1">
            {contentPreview.trim() ? (
              <RefineContext.Provider value={refineContextValue}>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                  {contentPreview}
                </ReactMarkdown>
              </RefineContext.Provider>
            ) : (
              <p className="text-[12.5px] text-ink-3 italic">
                {i18n._(msg`Nessuna anteprima disponibile.`)}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Destinations row — interactive picker OR read-only chip strip */}
      <div className="border-hairline border-t px-4 py-3">
        {destination ? (
          <DestinationPicker
            availableDestinations={renderableDestinations}
            value={destination.value}
            onChange={destination.onChange}
            folders={destination.folders}
            isLoadingFolders={destination.isLoadingFolders}
            onOpenIntegrations={destination.onOpenIntegrations}
            disabled={destination.disabled}
          />
        ) : (
          <div>
            <div className="mb-1.5 font-medium text-[10px] text-ink-3 uppercase tracking-[0.06em]">
              {i18n._(msg`Salva in`)}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {destinations.map((d) => {
                const DestIcon = DESTINATION_ICON[d] ?? HardDrive;
                const destLabel = DESTINATION_LABEL[d];
                return (
                  <Badge
                    key={d}
                    variant="outline"
                    className="gap-1 px-2 py-0.5 text-[11.5px]"
                  >
                    <DestIcon className="h-3 w-3" aria-hidden />
                    {destLabel ? i18n._(destLabel) : d}
                  </Badge>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
DocumentCreatePreview.displayName = "DocumentCreatePreview";

// ─── WYSIWYG slide thumbnails (sandbox decks) ───────────────────────────────

/**
 * Grid of rendered slide thumbnails for a sandbox-authored deck. Each cell is
 * the ACTUAL slide (brand kit, layout, Visual-QA fixes) so the user reviews the
 * real deck before Confirm. Editorial Ando styling — paper cards on cream,
 * hairline borders, a subtle ink index chip; no bright blocks.
 */
const SlideThumbnailGrid: React.FC<{
  readonly images: ReadonlyArray<{ readonly index: number; readonly url: string }>;
  readonly loading: boolean;
}> = ({ images, loading }) => {
  const { i18n } = useLingui();
  // Still rendering server-side → 16:9 skeletons so the section reserves space
  // (no layout shift when the thumbnails land).
  if (loading && images.length === 0) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="aspect-video animate-pulse rounded-lg border border-hairline bg-cream-deep/40"
          />
        ))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {images.map((s) => (
        <figure
          key={s.index}
          className="group relative overflow-hidden rounded-lg border border-hairline bg-white"
        >
          <img
            src={s.url}
            alt={i18n._(msg`Slide ${s.index}`)}
            loading="lazy"
            className="block h-auto w-full"
          />
          <figcaption
            aria-hidden
            className="absolute left-1.5 top-1.5 grid h-5 min-w-5 place-items-center rounded-full bg-ink-1/75 px-1 font-medium text-[10px] text-white tabular-nums"
          >
            {s.index}
          </figcaption>
        </figure>
      ))}
    </div>
  );
};
SlideThumbnailGrid.displayName = "DocumentCreatePreview.SlideThumbnailGrid";

// ─── Per-section wrapper ────────────────────────────────────────────────────

/**
 * Carries the "live" refine-panel state (open section, typed text, busy
 * flag) OUTSIDE the ``components`` map passed to ``ReactMarkdown``. The h1-h4
 * renderers below must keep a STABLE function identity across keystrokes:
 * react-markdown treats a changed ``components`` entry as a different
 * element TYPE and unmounts/remounts the subtree, which used to kill the
 * refinement ``<textarea>``'s focus after every character typed (it was a
 * fresh DOM node each render). Reading the live values via context instead
 * of closures lets ``SectionWrapper`` re-render in place without the parent
 * heading component ever changing identity.
 */
interface RefineContextValue {
  readonly openSection: string | null;
  readonly refineText: string;
  readonly refineBusy: boolean;
  readonly textareaRef: React.MutableRefObject<HTMLTextAreaElement | null>;
  readonly onOpen: (heading: string) => void;
  readonly onClose: () => void;
  readonly onTextChange: (value: string) => void;
  readonly onSubmit: () => void;
}
const RefineContext = React.createContext<RefineContextValue | null>(null);

interface SectionWrapperProps {
  readonly heading: string;
  /** The heading element to render (``h1``–``h4``) — every level is editable. */
  readonly headingTag: "h1" | "h2" | "h3" | "h4";
  /** Per-level typography for the heading text. */
  readonly headingClassName: string;
  readonly showAffordance: boolean;
  readonly children: React.ReactNode;
}

/**
 * Wraps a markdown H2 + everything that follows (until the next H2) in
 * a hover-aware section so the refinement chip can reveal in the top-
 * right. Because react-markdown emits flat children, we ONLY wrap the
 * heading itself + leave the following paragraphs as siblings; the
 * group-hover surface is just the heading row (Notion's "hover the
 * heading to reveal section actions" pattern).
 */
const SectionWrapper: React.FC<SectionWrapperProps> = ({
  heading,
  headingTag,
  headingClassName,
  showAffordance,
  children,
}) => {
  const { i18n } = useLingui();
  const HeadingTag = headingTag;
  // Live refine-panel state lives in context (not props baked into the
  // react-markdown ``components`` map). See the comment on
  // ``RefineContextValue`` above.
  const refine = React.useContext(RefineContext);
  const isOpen = refine?.openSection === heading;
  const isBusy = Boolean(isOpen && refine?.refineBusy);
  const refineText = refine?.refineText ?? "";
  return (
    <section
      className="group -mx-2 relative mt-6 mb-2 first:mt-0"
      data-section-heading={heading}
    >
      <div className="relative flex items-center gap-2 rounded-md px-2 py-1 transition-colors duration-200 hover:bg-ando-orange-soft/15">
        <HeadingTag className={headingClassName}>
          {children}
        </HeadingTag>
        {showAffordance ? (
          <button
            type="button"
            onClick={() => refine?.onOpen(heading)}
            aria-label={i18n._(msg`Modifica con Ando la sezione: ${heading}`)}
            className={cn(
              "ml-auto inline-flex shrink-0 items-center gap-1 rounded-md border border-hairline bg-card px-2 py-1 text-[11px] text-ink-3",
              "transition-[opacity,border-color,color,background-color] duration-200 ease-out",
              "hover:border-ando-orange hover:bg-ando-orange-soft hover:text-ando-orange",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange focus-visible:ring-offset-1",
              isOpen
                ? "border-ando-orange bg-ando-orange-soft text-ando-orange opacity-100"
                : "opacity-0 focus-visible:opacity-100 group-hover:opacity-100",
            )}
          >
            <PencilLine className="h-3 w-3" aria-hidden strokeWidth={2} />
            <span>{i18n._(msg`Modifica`)}</span>
          </button>
        ) : null}
      </div>

      {isOpen && showAffordance ? (
        <div className="mt-2 ml-2 rounded-md border border-hairline border-l-2 border-l-ando-orange/55 bg-card/60 px-3 py-3">
          <label
            htmlFor={`refine-${heading}`}
            className="mb-1.5 block font-medium text-[10px] text-ando-orange uppercase tracking-[0.06em]"
          >
            {i18n._(msg`Modifica con Ando — ${heading}`)}
          </label>
          <textarea
            ref={refine?.textareaRef}
            id={`refine-${heading}`}
            value={refineText}
            onChange={(e) => refine?.onTextChange(e.target.value)}
            placeholder={i18n._(
              msg`Es. Rendi più formale, aggiungi una clausola di riservatezza, abbrevia…`,
            )}
            rows={3}
            disabled={isBusy}
            className={cn(
              "w-full resize-y rounded-md border border-hairline bg-white px-3 py-2 text-[13px] text-ink-1 leading-relaxed",
              "placeholder:text-ink-3",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange",
              "disabled:cursor-not-allowed disabled:opacity-60",
            )}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                refine?.onClose();
              }
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                refine?.onSubmit();
              }
            }}
          />
          <div className="mt-2 flex items-center justify-between gap-2">
            <span className="text-[11px] text-ink-3">
              {i18n._(msg`⌘/Ctrl + Invio per inviare · Esc per annullare`)}
            </span>
            <div className="flex items-center gap-1.5">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => refine?.onClose()}
                disabled={isBusy}
              >
                {i18n._(msg`Annulla`)}
              </Button>
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={() => refine?.onSubmit()}
                disabled={isBusy || !refineText.trim()}
                aria-busy={isBusy || undefined}
              >
                {isBusy ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                ) : (
                  <AndoLogo size={14} aria-hidden />
                )}
                {isBusy ? i18n._(msg`Rigenero…`) : i18n._(msg`Rigenera`)}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};
SectionWrapper.displayName = "DocumentCreatePreview.SectionWrapper";
